import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Icon,
  IconButton,
  Modal,
  Radio,
  Skeleton,
  Stack,
  Text,
  Tooltip
} from '@efast_public/fjd-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { ApiMember } from '../api/govos.types';
import { useNotification } from '../shared/notification/useNotification';

interface MembersApiResponse {
  result: string;
  members: ApiMember[];
}

export interface Member {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'member' | 'moderator';
  icon?: string;
}

interface MemberChange {
  memberId: string;
  newRole?: 'member' | 'moderator';
  remove?: boolean;
  email?: string;
}

interface EditMembersModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  startAppId: number;
}

export function EditMembersModal({
  onClose,
  onSuccess,
  isOpen,
  startAppId
}: EditMembersModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUser } = useNotification();
  const [memberChanges, setMemberChanges] = useState<MemberChange[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch members from API
  const {
    data: members = [],
    isLoading,
    refetch
  } = useQuery({
    queryKey: [GovosApiRoutes.members(startAppId)],
    queryFn: async (): Promise<Member[]> => {
      const response = await govosClient.get<MembersApiResponse>(
        GovosApiRoutes.members(startAppId)
      );
      const data = response.data;

      // Handle response structure: { result: "success", members: [...] }
      if (data?.result === 'success' && Array.isArray(data.members)) {
        // Map API response to Member interface
        const result: Member[] = data.members.map((member: ApiMember) => {
          // Map role values: 6 = owner, 5 = moderator, 4 = member
          let role: 'owner' | 'member' | 'moderator' = 'member';
          if (member.role === 6) {
            role = 'owner';
          } else if (member.role === 5) {
            role = 'moderator';
          } else if (member.role === 4) {
            role = 'member';
          }

          return {
            id: String(member.userid),
            name: member.dispname,
            email: member.email,
            role,
            icon: member.icon
          };
        });
        // Sort: owner first, then moderator, then member
        return result.sort((a, b) => {
          const roleOrder = { owner: 0, moderator: 1, member: 2 };
          return roleOrder[a.role] - roleOrder[b.role];
        });
      }

      return [];
    },
    enabled: isOpen && startAppId > 0
  });

  // Refetch when modal opens
  React.useEffect(() => {
    if (isOpen && startAppId > 0) {
      refetch();
    }
  }, [isOpen, startAppId, refetch]);

  const handleRoleChange = (
    memberId: string,
    newRole: 'member' | 'moderator',
    email: string
  ) => {
    setErrorMessage(null);
    setMemberChanges((prev) => {
      const existing = prev.find((c) => c.memberId === memberId);
      if (existing) {
        return prev.map((c) =>
          c.memberId === memberId ? { ...c, newRole, email, remove: false } : c
        );
      }
      return [...prev, { memberId, newRole, email }];
    });
  };

  const handleRemoveToggle = (memberId: string, remove: boolean) => {
    setErrorMessage(null);
    setMemberChanges((prev) => {
      const existing = prev.find((c) => c.memberId === memberId);
      if (existing) {
        return prev.map((c) =>
          c.memberId === memberId ? { ...c, remove } : c
        );
      }
      return [...prev, { memberId, remove }];
    });
  };

  // Mutation to apply all changes
  const { mutate: applyChanges, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const throwIfBodyError = (data: unknown, fallback: string) => {
        const body = data as
          | { result?: string; error?: { msg?: string; code?: number } }
          | undefined;
        if (body?.result === 'error') {
          throw new Error(body.error?.msg || fallback);
        }
      };
      const promises = memberChanges.map(async (change) => {
        if (change.remove) {
          const res = await govosClient.delete(
            GovosApiRoutes.removeMember(startAppId, change.memberId)
          );
          throwIfBodyError(res?.data, t('EditMembersModal.errorMessage'));
        } else if (change.newRole) {
          const roleValue = change.newRole === 'member' ? 4 : 5;
          const res = await govosClient.put(
            GovosApiRoutes.updateMemberRole(startAppId, change.memberId),
            { role: roleValue }
          );
          throwIfBodyError(res?.data, t('EditMembersModal.errorMessage'));
        }
      });
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GovosApiRoutes.members(startAppId)]
      });
      setMemberChanges([]);
      notifyUser({
        intent: 'success',
        title: t('EditMembersModal.successMessage')
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      let message: string;
      if (error instanceof Error && error.message) {
        message = error.message;
      } else {
        const axiosError = error as {
          response?: {
            data?: {
              error?: { msg?: string; code?: number };
              message?: string;
            };
            status?: number;
          };
        };
        message =
          axiosError.response?.data?.error?.msg ||
          axiosError.response?.data?.message ||
          t('EditMembersModal.errorMessage');
      }
      setErrorMessage(message);
    }
  });

  const handleSave = () => {
    applyChanges();
  };

  const hasChanges = memberChanges.length > 0;

  if (isLoading) {
    return (
      <Modal open={isOpen} onClose={onClose}>
        <Modal.Header id="modal-header">
          <Text size="xl" strong>
            {t('EditMembersModal.title')}
          </Text>
        </Modal.Header>
        <Modal.Content>
          <div style={{ minWidth: '540px' }}>
            <Stack spacing="l">
              <Skeleton height={20} width={160} />
              {[0, 1, 2].map((i) => (
                <Stack
                  key={i}
                  direction="horizontal"
                  spacing="l"
                  alignItems="center"
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '16px',
                    paddingTop: '8px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Skeleton height={20} />
                  </div>
                  <Stack
                    spacing="xs"
                    style={{ flexShrink: 0, minWidth: '160px' }}
                  >
                    <Skeleton height={20} />
                    <Skeleton height={20} />
                  </Stack>
                  <div style={{ flexShrink: 0 }}>
                    <Skeleton height={20} width={100} />
                  </div>
                </Stack>
              ))}
            </Stack>
          </div>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditMembersModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ minWidth: '540px' }}>
          <Stack spacing="l">
            {errorMessage && (
              <Alert
                variant="error"
                message={errorMessage}
                onClose={() => setErrorMessage(null)}
              />
            )}
            <Stack direction="horizontal" spacing="xs" alignItems="center">
              <Text size="s" strong>
                {t('EditMembersModal.memberList')}
              </Text>
              <Tooltip
                tooltip={t(
                  'ZustaendigeStelle.memberManagement.roleDescriptions'
                )}
              >
                <IconButton
                  variant="primary-text"
                  label=""
                  icon="information-filled"
                  size="sm"
                />
              </Tooltip>
            </Stack>
            {members.map((member: Member) => {
              const isOwner = member.role === 'owner';
              const change = memberChanges.find(
                (c) => c.memberId === member.id
              );
              const currentRole = change?.newRole || member.role;
              const isMarkedForRemoval = change?.remove || false;
              const hasPendingRoleChange =
                !!change?.newRole && change.newRole !== member.role;

              return (
                <Stack
                  key={member.id}
                  direction="horizontal"
                  spacing="l"
                  alignItems="center"
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '16px',
                    paddingTop: '8px'
                  }}
                >
                  <Stack spacing="xs" style={{ flex: 1, minWidth: 0 }}>
                    <Text strong>{member.name}</Text>
                  </Stack>

                  <Stack
                    spacing="xs"
                    style={{ flexShrink: 0, minWidth: '160px' }}
                  >
                    {isOwner ? (
                      <Stack
                        direction="horizontal"
                        spacing="xs"
                        alignItems="center"
                      >
                        <Icon name="star-filled" size="sm" />
                        <Text size="s">
                          {t('ZustaendigeStelle.memberManagement.role.owner')}
                        </Text>
                      </Stack>
                    ) : (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Icon
                            name="user"
                            size="sm"
                            style={{ flexShrink: 0 }}
                          />
                          <Radio
                            name={`role-${member.id}`}
                            value="member"
                            checked={currentRole === 'member'}
                            onChange={() =>
                              handleRoleChange(
                                member.id,
                                'member',
                                member.email
                              )
                            }
                            label={t(
                              'ZustaendigeStelle.memberManagement.role.member'
                            )}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Icon
                            name="user-role"
                            size="sm"
                            style={{ flexShrink: 0 }}
                          />
                          <Radio
                            name={`role-${member.id}`}
                            value="moderator"
                            checked={currentRole === 'moderator'}
                            onChange={() =>
                              handleRoleChange(
                                member.id,
                                'moderator',
                                member.email
                              )
                            }
                            label={t(
                              'ZustaendigeStelle.memberManagement.role.moderator'
                            )}
                          />
                        </div>
                      </>
                    )}
                  </Stack>

                  <div
                    style={{
                      flexShrink: 0,
                      visibility: isOwner ? 'hidden' : 'visible'
                    }}
                  >
                    <Checkbox
                      checked={isMarkedForRemoval}
                      disabled={hasPendingRoleChange}
                      onChange={(e) =>
                        handleRemoveToggle(
                          member.id,
                          (e.target as HTMLInputElement).checked
                        )
                      }
                      label={t('EditMembersModal.removeMember')}
                    />
                  </div>
                </Stack>
              );
            })}
          </Stack>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Stack spacing="xs" style={{ width: '100%' }}>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            style={{ width: '100%' }}
          >
            {isSaving
              ? t('EditMembersModal.loadingMessage')
              : t('EditMembersModal.actions.apply')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            style={{ width: '100%' }}
          >
            {t('EditMembersModal.actions.cancel')}
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}
